/* eslint-disable no-console */
/**
 * Favicon Badge Utility
 * Adds a red notification badge with a number to the favicon
 */

let originalFaviconUrl: string | null = null;
let canvas: HTMLCanvasElement | null = null;
let currentBadgeCount: number | null = null;

function getFaviconElement(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");

  if (link == null) {
    link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    document.head.appendChild(link);
  }

  return link;
}

function storeOriginalFavicon(): void {
  if (originalFaviconUrl != null) {
    return;
  }

  const link = getFaviconElement();
  originalFaviconUrl = link.href ?? '/favicon.svg';
}

function getCanvas(): HTMLCanvasElement {
  if (canvas == null) {
    canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
  }

  return canvas;
}

function drawBadge(ctx: CanvasRenderingContext2D, count: number, size: number): void {
  const badgeSize = size * 0.7;
  const badgeX = size - badgeSize;
  const badgeY = 0;

  // Draw red circle background
  ctx.beginPath();
  ctx.arc(badgeX + badgeSize / 2, badgeY + badgeSize / 2, badgeSize / 2, 0, 2 * Math.PI);
  ctx.fillStyle = '#EF4444';
  ctx.fill();

  // Draw white border
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Draw the number
  const displayText = count > 99 ? '99+' : String(count);
  const fontSize = count > 99 ? badgeSize * 0.45 : badgeSize * 0.6;

  ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(displayText, badgeX + badgeSize / 2, badgeY + badgeSize / 2 + 1);
}

export function setFaviconBadge(count: number | null): void {
  storeOriginalFavicon();

  // If count is 0 or null, restore original favicon
  if (count == null || count <= 0) {
    // eslint-disable-next-line no-use-before-define
    clearFaviconBadge();
    return;
  }

  // Don't redraw if the count hasn't changed
  if (currentBadgeCount === count) {
    return;
  }

  currentBadgeCount = count;

  const cvs = getCanvas();
  const ctx = cvs.getContext('2d');

  if (ctx == null) {
    console.warn('Could not get canvas 2d context for favicon badge');
    return;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';

  img.addEventListener('load', function () {
    const size = cvs.width;

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    // Draw original favicon
    ctx.drawImage(img, 0, 0, size, size);

    // Draw badge
    drawBadge(ctx, count, size);

    // Update favicon
    const link = getFaviconElement();
    link.type = 'image/png';
    link.href = cvs.toDataURL('image/png');
  });

  img.addEventListener('error', function () {
    console.warn('Failed to load favicon for badge overlay');
  });

  img.src = originalFaviconUrl ?? '/favicon.svg';
}

export function clearFaviconBadge(): void {
  if (originalFaviconUrl == null) {
    return;
  }

  currentBadgeCount = null;

  const link = getFaviconElement();
  link.href = originalFaviconUrl;
}

export function getFaviconBadgeCount(): number | null {
  return currentBadgeCount;
}

export function incrementFaviconBadge(amount: number = 1): void {
  const current = currentBadgeCount ?? 0;
  setFaviconBadge(current + amount);
}

export function decrementFaviconBadge(amount: number = 1): void {
  const current = currentBadgeCount ?? 0;
  const newCount = Math.max(0, current - amount);
  setFaviconBadge(newCount);
}

export function playNotificationSound(): void {
  const audio = new Audio('/notif-sound.mp3');
  audio.play().catch(function () {
    console.warn('Failed to play notification sound');
  });
}
